/** Pieces both report pages are built from: the change mark, the store split,
 *  the metric and app tables, the share bars, the coverage list. */

import { useState, type ReactNode } from 'react'
import type { AppRow, ByStore, Cmp, Coverage, CountryRow, Fmt, KeywordMove, MetricRow, Payment, Review, Share, StoreKey } from '@/api/client'
import { Button, Delta, DataTable, Legend, StackedBar, TonePill, type Column } from '@/ui'
import { cn } from '@/lib/utils'
import { KIND_LABEL, STORES, STORE_FILL, STORE_LABEL, changeText, changeTone, fmtValue, md, num, pct, rankText, sumStore, won } from './format'

/** A change with its colour: ▲/▼ and the signed amount. */
export function Change({ fmt, cur, prev, goodUp = true, className }: { fmt: Fmt; cur: number | null | undefined; prev: number | null | undefined; goodUp?: boolean; className?: string }) {
  const text = changeText(fmt, cur, prev)
  const tone = changeTone(cur, prev, goodUp)
  if (!text) return <span className={cn('text-muted-foreground', className)}>—</span>
  return (
    <span className={cn('whitespace-nowrap font-medium tabular-nums', tone === 'up' ? 'text-delta-up' : tone === 'down' ? 'text-delta-down' : 'text-muted-foreground', className)}>
      <span aria-hidden>{text.startsWith('+') ? '▲ ' : '▼ '}</span>
      {text}
    </span>
  )
}

/** The three stores as swatch + name + amount, in one line that wraps. A
 *  store in `pending` has not published the day yet. */
export function StoreSplit({ revenue, noun = '수익', pending, className }: { revenue: ByStore; noun?: '수익' | '매출'; pending?: StoreKey[]; className?: string }) {
  return (
    <ul aria-label={`스토어별 ${noun}`} className={cn('flex flex-wrap gap-x-3 gap-y-0.5', className)}>
      {STORES.map((k) => (
        <li key={k} className="inline-flex items-center gap-1 whitespace-nowrap">
          <span aria-hidden className={cn('h-2 w-2 rounded-sm', STORE_FILL[k])} />
          {STORE_LABEL[k]} {pending?.includes(k) ? '집계 전' : won(revenue[k])}
        </li>
      ))}
    </ul>
  )
}

/** One bar split into the three stores, with the legend under it. */
export function StoreBar({ revenue, noun = '수익', className }: { revenue: ByStore; noun?: '수익' | '매출'; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      <StackedBar
        label={`스토어별 ${noun} ${STORES.map((k) => `${STORE_LABEL[k]} ${won(revenue[k])}`).join(', ')}`}
        height={10}
        segments={STORES.map((k) => ({ key: k, label: STORE_LABEL[k], value: revenue[k], fill: STORE_FILL[k] }))}
      />
      <Legend items={STORES.map((k) => ({ label: STORE_LABEL[k], swatch: STORE_FILL[k], value: won(revenue[k]) }))} />
    </div>
  )
}

export function Section({ title, hint, children, className }: { title: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-section font-semibold">{title}</h2>
        {hint && <span className="text-meta text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  )
}

export function Facts({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <ul aria-label="요약" className="space-y-1.5 rounded-lg border border-border bg-card p-4 text-body">
      {items.map((f, i) => (
        <li key={i} className="flex gap-2">
          <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
          <span className="min-w-0 break-words">{f}</span>
        </li>
      ))}
    </ul>
  )
}

const platformText = (p: AppRow['platform']) => (p === 'appstore' ? 'iOS' : p === 'android' ? 'Android' : '')

function PlatformBadge({ p }: { p: AppRow['platform'] }) {
  if (!p) return null
  return <TonePill tone={p === 'appstore' ? 'web' : 'published'} label={platformText(p)} />
}

export function MetricTable({ rows, prevLabel, curLabel, label = '지표' }: { rows: MetricRow[]; curLabel: string; prevLabel: string; label?: string }) {
  const cols: Column<MetricRow>[] = [
    {
      key: 'label',
      header: '지표',
      card: 'title',
      cell: (r) => (
        <span>
          {r.label}
          {r.note && <span className="ml-1.5 text-meta font-normal text-muted-foreground">{r.note}</span>}
        </span>
      ),
    },
    { key: 'cur', header: curLabel, align: 'right', cell: (r) => <span className="tabular-nums">{fmtValue(r.fmt, r.cur)}</span> },
    { key: 'prev', header: prevLabel, align: 'right', cell: (r) => <span className="tabular-nums text-muted-foreground">{fmtValue(r.fmt, r.prev)}</span> },
    {
      key: 'delta',
      header: '변화',
      align: 'right',
      cell: (r) => (r.partial ? <span className="text-meta text-muted-foreground">지금까지</span> : <Change fmt={r.fmt} cur={r.cur} prev={r.prev} goodUp={r.good_up !== false} />),
    },
  ]
  return <DataTable columns={cols} rows={rows} rowKey={(r) => r.key} label={label} />
}

function appName(r: AppRow): ReactNode {
  if (r.row === 'rest') return `그 외 ${r.rest_count ?? ''}개`.trim()
  if (r.row === 'total') return '합계'
  return r.name
}

/** The first `limit` rows of a table, with a button for the rest. Total rows
 *  always stay. Without a limit every row shows. */
function useTop<T extends { row: string }>(rows: T[], limit: number | undefined, unit: string) {
  const [open, setOpen] = useState(false)
  const body = rows.filter((r) => r.row !== 'total')
  const hidden = limit && !open ? Math.max(0, body.length - limit) : 0
  const shown = hidden > 0 ? [...body.slice(0, limit), ...rows.filter((r) => r.row === 'total')] : rows
  const more =
    limit && body.length > limit ? (
      <Button variant="ghost" size="sm" className="mt-2" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? '접기' : `나머지 ${hidden}${unit} 보기`}
      </Button>
    ) : null
  return { shown, more }
}

/** An amount with its per-store line under it (only the stores that earned). */
function MoneyCell({ cur, byStore }: { cur: number | null | undefined; byStore?: ByStore }) {
  return (
    <div className="tabular-nums">
      <div>{won(cur)}</div>
      {byStore && sumStore(byStore) > 0 && (
        <div className="text-micro text-muted-foreground">
          {STORES.filter((k) => byStore[k] > 0).map((k) => `${STORE_LABEL[k]} ${won(byStore[k])}`).join(' · ')}
        </div>
      )}
    </div>
  )
}

/** The two words, once, for the pages that show both. */
export function SalesNote({ className }: { className?: string }) {
  return <p className={cn('text-meta text-muted-foreground', className)}>매출 = 고객 결제액(세금 포함, 수수료 빼기 전) · 수익 = 스토어 수수료·세금을 뺀 실수령</p>
}

export function AppTable({ rows, label = '앱별', cmp = true, limit }: { rows: AppRow[]; label?: string; cmp?: boolean; limit?: number }) {
  const { shown, more } = useTop(rows, limit, '개')
  const cols: Column<AppRow>[] = [
    {
      key: 'name',
      header: '앱',
      card: 'title',
      cell: (r) => (
        <span className={cn('inline-flex flex-wrap items-center gap-1.5', r.row === 'total' && 'font-semibold')}>
          {appName(r)}
          {r.row === 'app' && <PlatformBadge p={r.platform} />}
        </span>
      ),
    },
    { key: 'sales', header: '매출', align: 'right', cell: (r) => <MoneyCell cur={r.sales?.cur} byStore={r.sales_by_store} /> },
    { key: 'revenue', header: '수익', align: 'right', cell: (r) => <MoneyCell cur={r.revenue.cur} byStore={r.revenue_by_store} /> },
    ...(cmp
      ? [{ key: 'rdelta', header: '수익 변화', align: 'right' as const, cell: (r: AppRow) => <Change fmt="krw" cur={r.revenue.cur} prev={r.revenue.prev} /> }]
      : []),
    { key: 'downloads', header: '신규 DL', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.downloads.cur)}</span> },
    ...(cmp
      ? [{ key: 'ddelta', header: 'DL 변화', align: 'right' as const, cell: (r: AppRow) => <Change fmt="int" cur={r.downloads.cur} prev={r.downloads.prev} /> }]
      : []),
    { key: 'payments', header: '결제', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.payments)}</span> },
    { key: 'impressions', header: '노출', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.impressions)}</span> },
    { key: 'score', header: '키워드 점수', align: 'right', cell: (r) => <span className="tabular-nums">{r.score ? num(r.score.cur, 1) : '—'}</span> },
    { key: 'sdelta', header: '점수 변화', align: 'right', cell: (r) => <Change fmt="score" cur={r.score?.cur} prev={r.score?.prev} /> },
  ]
  return (
    <>
      <DataTable
        columns={cols}
        rows={shown}
        rowKey={(r) => `${r.row}:${r.app_id ?? r.name}`}
        label={label}
        rowClassName={(r) => (r.row === 'total' ? 'bg-muted/40 font-semibold' : undefined)}
      />
      {more}
    </>
  )
}

function countryName(r: CountryRow): ReactNode {
  if (r.row === 'rest') return `그 외 ${r.rest_count ?? ''}개국`.trim()
  if (r.row === 'total') return '합계'
  return r.country
}

export function CountryTable({ rows, label = '국가별', limit }: { rows: CountryRow[]; label?: string; limit?: number }) {
  const { shown, more } = useTop(rows, limit, '개국')
  const cols: Column<CountryRow>[] = [
    { key: 'country', header: '국가', card: 'title', cell: (r) => <span className={cn(r.row === 'total' && 'font-semibold')}>{countryName(r)}</span> },
    { key: 'sales', header: '매출', align: 'right', cell: (r) => <span className="tabular-nums">{won(r.sales_krw)}</span> },
    { key: 'revenue', header: '수익', align: 'right', cell: (r) => <span className="tabular-nums">{won(r.revenue_krw)}</span> },
    {
      key: 'native',
      header: '현지 통화 (고객 결제)',
      align: 'right',
      card: 'line',
      cell: (r) =>
        !r.sales_native || r.sales_native.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums text-muted-foreground">{r.sales_native.map((n) => `${n.currency} ${num(n.amount, 2)}`).join(' · ')}</span>
        ),
    },
    { key: 'downloads', header: '신규 DL', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.downloads)}</span> },
    { key: 'impressions', header: '노출', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.impressions)}</span> },
    { key: 'first', header: '첫 DL', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.first_downloads)}</span> },
    { key: 'cvr', header: '전환율', align: 'right', cell: (r) => <span className="tabular-nums">{pct(r.cvr)}</span> },
  ]
  return (
    <>
      <DataTable
        columns={cols}
        rows={shown}
        rowKey={(r) => `${r.row}:${r.country}`}
        label={label}
        rowClassName={(r) => (r.row === 'total' ? 'bg-muted/40 font-semibold' : undefined)}
      />
      {more}
    </>
  )
}

/** Shares as rows with a bar: the app, kind and store splits. */
export function ShareBars({ items, label, fill = 'bg-chart-1' }: { items: Share[]; label: string; fill?: string }) {
  const total = items.reduce((s, x) => s + Math.max(0, x.krw), 0)
  if (items.length === 0) return <p className="text-table text-muted-foreground">수익이 없다.</p>
  return (
    <ul aria-label={label} className="space-y-2">
      {items.map((s) => {
        const share = total > 0 ? Math.max(0, s.krw) / total : 0
        return (
          <li key={s.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 text-table">
            <span className="truncate">{KIND_LABEL[s.label] ?? s.label}</span>
            <span className="tabular-nums">
              {won(s.krw)} <span className="text-muted-foreground">{(share * 100).toFixed(1)}%</span>
            </span>
            <span aria-hidden className="col-span-2 block h-1.5 overflow-hidden rounded-full bg-muted">
              <span className={cn('block h-full rounded-full', fill)} style={{ width: `${share * 100}%` }} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export function PaymentsTable({ rows, label = '결제 내역' }: { rows: Payment[]; label?: string }) {
  const cols: Column<Payment>[] = [
    { key: 'date', header: '날짜', card: 'sub', cell: (r) => <span className="tabular-nums">{md(r.date)}</span> },
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {r.app}
          {r.refund && <TonePill tone="failed" label="환불" />}
        </span>
      ),
    },
    { key: 'store', header: '스토어', card: 'meta', cell: (r) => STORE_LABEL[r.store] },
    { key: 'kind', header: '종류', cell: (r) => KIND_LABEL[r.kind] ?? r.kind },
    { key: 'country', header: '국가', cell: (r) => r.country },
    { key: 'units', header: '건수', align: 'right', cell: (r) => <span className="tabular-nums">{num(r.units)}</span> },
    {
      key: 'amount',
      header: '현지 금액',
      align: 'right',
      cell: (r) =>
        r.sales_amount == null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums text-muted-foreground">{r.sales_currency} {num(r.sales_amount, 2)}</span>
        ),
    },
    { key: 'sales_krw', header: '매출(원화)', align: 'right', cell: (r) => (r.sales_krw == null ? <span className="text-muted-foreground">—</span> : <span className={cn('tabular-nums', r.refund && 'text-delta-down')}>{won(r.sales_krw)}</span>) },
    { key: 'krw', header: '수익(원화)', align: 'right', cell: (r) => <span className={cn('tabular-nums', r.refund && 'text-delta-down')}>{won(r.krw)}</span> },
  ]
  return <DataTable columns={cols} rows={rows} rowKey={(r) => `${r.date}|${r.store}|${r.app}|${r.kind}|${r.country}|${r.amount}|${r.refund}`} label={label} empty={<p className="text-table text-muted-foreground">결제가 없다.</p>} />
}

export function ReviewList({ rows }: { rows: Review[] }) {
  if (rows.length === 0) return <p className="text-table text-muted-foreground">새 리뷰가 없다.</p>
  return (
    <ul aria-label="새 리뷰" className="divide-y divide-border rounded-lg border border-border bg-card">
      {rows.map((r, i) => (
        <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 text-table">
          <span className="whitespace-nowrap tabular-nums" aria-label={`별 ${r.rating}개`}>
            {'★'.repeat(Math.max(0, Math.min(5, r.rating)))}
            <span className="text-muted-foreground/40">{'★'.repeat(Math.max(0, 5 - r.rating))}</span>
          </span>
          <span className="font-medium">{r.app}</span>
          <span className="text-muted-foreground">{r.territory}</span>
          <span className="min-w-0 flex-1 break-words">{r.title}</span>
          <span className="tabular-nums text-meta text-muted-foreground">{r.created_at.slice(0, 10)}</span>
        </li>
      ))}
    </ul>
  )
}

export function KeywordMoves({ rows, dir }: { rows: KeywordMove[]; dir: 'up' | 'down' }) {
  const label = dir === 'up' ? '오른 키워드' : '내린 키워드'
  if (rows.length === 0) return <p className="text-table text-muted-foreground">{label}가 없다.</p>
  return (
    <ul aria-label={label} className="divide-y divide-border rounded-lg border border-border bg-card">
      {rows.map((r, i) => (
        <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 text-table">
          <span className="min-w-0 flex-1 break-words">
            <span className="font-medium">{r.term}</span>
            <span className="ml-2 text-muted-foreground">
              {r.app} · {r.country.toUpperCase()} · 인기도 {r.pop}
            </span>
          </span>
          <Delta status={dir} delta={Math.abs(r.delta)} />
          <span className="whitespace-nowrap tabular-nums text-muted-foreground">
            {rankText(r.rank_prev)} → {rankText(r.rank_cur)}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function CoverageList({ rows }: { rows: Coverage[] }) {
  return (
    <ul aria-label="데이터 범위" className="divide-y divide-border rounded-lg border border-border bg-card">
      {rows.map((c) => (
        <li key={c.source} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 text-table">
          <span className="min-w-[7rem] font-medium">{c.label}</span>
          <span className="tabular-nums text-muted-foreground">{c.first ? `${c.first} ~ ${c.last ?? ''}` : '데이터 없음'}</span>
          <TonePill tone={c.covers ? 'published' : 'deadline'} label={c.covers ? '충분' : '부족'} />
          {c.note && <span className="min-w-0 basis-full break-words text-meta text-muted-foreground sm:basis-auto">{c.note}</span>}
        </li>
      ))}
    </ul>
  )
}

export function cmpOf(c: Cmp | null | undefined): { cur: number | null; prev: number | null } {
  return c ?? { cur: null, prev: null }
}

/** The grid the five top tiles sit in: one row from 1440px, otherwise three
 *  (two on a phone) with the last tile taking the leftover width. The value is
 *  a size smaller in the five-column row so a seven-digit amount still fits. */
export const KPI_GRID = 'grid grid-cols-2 gap-3 lg:grid-cols-3 min-[1440px]:grid-cols-5'
export const KPI_LAST = 'col-span-2 lg:col-span-2 min-[1440px]:col-span-1'

export function Kpi({ label, value, className, children }: { label: string; value: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4', className)}>
      <div className="text-meta text-muted-foreground">{label}</div>
      <div className="mt-1.5 whitespace-nowrap text-stat font-semibold tabular-nums min-[1440px]:text-[1.375rem]">{value}</div>
      {children && <div className="mt-1.5 space-y-1 break-words text-meta text-muted-foreground">{children}</div>}
    </div>
  )
}
